import React, { useState } from 'react';
import { Trash2, ChevronDown, ChevronUp, Image, Maximize, Target, Sparkles } from 'lucide-react';
import { PatternStep, PromptOverride } from 'services/apiService';

export interface AdditionalPictureOverride {
  original_id: string;
  original_category: string;
  source: 'column' | 'fixed';
  column_name?: string;
  fixed_url?: string;
}

interface StepEditorProps {
  step: PatternStep;
  stepIndex: number;
  ruleId: string;
  selectedColumns: string[];
  promptOverrides: PromptOverride[];
  additionalPictureOverrides: AdditionalPictureOverride[];
  onAddPromptOverride: () => void;
  onUpdatePromptOverride: (overrideIndex: number, updates: Partial<PromptOverride>) => void;
  onRemovePromptOverride: (overrideIndex: number) => void;
  onUpdateAdditionalPictureOverride: (picIndex: number, updates: AdditionalPictureOverride) => void;
}

// Step Detail Card Component - Shows all details of a pattern step
const StepEditor: React.FC<StepEditorProps> = ({
  step,
  stepIndex,
  ruleId: _ruleId,
  selectedColumns,
  promptOverrides,
  additionalPictureOverrides,
  onAddPromptOverride,
  onUpdatePromptOverride,
  onRemovePromptOverride,
  onUpdateAdditionalPictureOverride
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Helper to safely convert value to displayable string
  const toDisplayString = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  // Extract data from meta field (preferred) or fallback to legacy top-level fields
  const meta = step.meta || {};
  const prompt = meta.prompt ?? step.prompt;
  const model = meta.model ?? step.model;
  const additionalPictures = meta.additionnal_pictures ?? step.additionnal_pictures;
  const targetSizeW = meta.target_size_w ?? step.target_size_w;
  const targetSizeH = meta.target_size_h ?? step.target_size_h;
  const factor = meta.factor ?? step.factor;
  const editHotspotX = meta.edit_hotspot_x ?? step.edit_hotspot_x;
  const editHotspotY = meta.edit_hotspot_y ?? step.edit_hotspot_y;

  // Additional meta fields
  const directions = meta.directions;
  const outputRatios = meta.output_ratios;
  const inputLanguage = meta.input_language;
  const outputLanguages = meta.output_languages;
  const formats = meta.formats;
  const useBrandGuidelines = meta.use_brand_guidelines;

  // Prefer JSON versions of configs if available
  const extendingConfigs = meta.extending_configs_json ?? meta.extending_configs;

  // Operation type display names
  const opDisplayNames: Record<string, string> = {
    'inpaint': 'AI Edit',
    'outpaint': 'Extend',
    'upscale': 'Upscale',
    'downscale': 'Downscale',
    'crop': 'Crop',
    'ai-crop': 'AI Crop',
    'translate': 'Translate',
    'reformat': 'Reformat',
    'remove-bg': 'Remove Background',
    'generate': 'Generate',
    'migrate-format': 'Reformat'
  };

  const opName = opDisplayNames[step.op] || step.op;

  // Check if step has any customizable content
  const hasPrompt = !!prompt;
  const hasAdditionalPictures = Array.isArray(additionalPictures) && additionalPictures.length > 0;
  const hasCustomizableContent = hasPrompt || hasAdditionalPictures;

  // Collect all displayable meta properties
  const metaEntries = Object.entries(meta).filter(([key, value]) => {
    // Skip already displayed fields and internal fields
    const skipFields = [
      'prompt', 'model', 'source_picture_id', 'additionnal_pictures',
      'target_size_w', 'target_size_h', 'factor', 'edit_hotspot_x',
      'edit_hotspot_y', 'directions', 'output_ratios', 'input_language',
      'output_languages', 'formats', 'use_brand_guidelines',
      // Skip non-JSON version if JSON version exists
      'extending_configs'
    ];
    // Also skip extending_configs_json as we display it separately
    if (key === 'extending_configs_json') return false;
    return !skipFields.includes(key) && value !== undefined && value !== null && value !== '';
  });

  // Helper to find override for a specific picture index
  const findOverrideForPicture = (picIndex: number): AdditionalPictureOverride | undefined => {
    const targetCategory = `step_${stepIndex}_pic_${picIndex}`;
    return additionalPictureOverrides.find(o => o.original_category === targetCategory);
  };

  return (
    <div className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
      {/* Step Header */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-700/30 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-purple-400 bg-purple-500/20 px-2 py-0.5 rounded">
              Step {stepIndex + 1}
            </span>
            <span className="text-sm font-medium text-white">
              {opName}
            </span>
            {model && (
              <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded">
                {model}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasPrompt && (
            <span className="text-xs text-green-400">Has prompt</span>
          )}
          {hasAdditionalPictures && (
            <span className="text-xs text-orange-400">
              {additionalPictures.length} ref image{additionalPictures.length > 1 ? 's' : ''}
            </span>
          )}
          {useBrandGuidelines && (
            <span className="text-xs text-purple-400">Brand</span>
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-700 p-3 space-y-3">
          {/* Step Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            {/* Operation */}
            <div className="bg-black/20 rounded p-2">
              <span className="text-gray-500 block mb-1">Operation</span>
              <span className="text-gray-200 font-mono">{step.op}</span>
            </div>

            {/* Model */}
            {model && (
              <div className="bg-black/20 rounded p-2">
                <span className="text-gray-500 block mb-1">Model</span>
                <span className="text-gray-200 font-mono">{model}</span>
              </div>
            )}

            {/* Dimensions */}
            {(targetSizeW || targetSizeH) && (
              <div className="bg-black/20 rounded p-2">
                <span className="text-gray-500 block mb-1 flex items-center gap-1">
                  <Maximize className="w-3 h-3" /> Size
                </span>
                <span className="text-gray-200 font-mono">
                  {targetSizeW} x {targetSizeH}
                </span>
              </div>
            )}

            {/* Scale Factor */}
            {factor && (
              <div className="bg-black/20 rounded p-2">
                <span className="text-gray-500 block mb-1">Factor</span>
                <span className="text-gray-200 font-mono">{factor}</span>
              </div>
            )}

            {/* Edit Hotspot */}
            {(editHotspotX !== undefined && editHotspotY !== undefined) && (
              <div className="bg-black/20 rounded p-2">
                <span className="text-gray-500 block mb-1 flex items-center gap-1">
                  <Target className="w-3 h-3" /> Hotspot
                </span>
                <span className="text-gray-200 font-mono">
                  ({editHotspotX}, {editHotspotY})
                </span>
              </div>
            )}

            {/* Directions (for outpaint) */}
            {directions && (
              <div className="bg-black/20 rounded p-2">
                <span className="text-gray-500 block mb-1">Directions</span>
                <span className="text-gray-200 font-mono">{toDisplayString(directions)}</span>
              </div>
            )}

            {/* Output Ratios (for reformat) */}
            {outputRatios && (
              <div className="bg-black/20 rounded p-2">
                <span className="text-gray-500 block mb-1">Output Ratios</span>
                <span className="text-gray-200 font-mono">{toDisplayString(outputRatios)}</span>
              </div>
            )}

            {/* Languages (for translate) */}
            {inputLanguage && (
              <div className="bg-black/20 rounded p-2">
                <span className="text-gray-500 block mb-1">Input Language</span>
                <span className="text-gray-200 font-mono">{inputLanguage}</span>
              </div>
            )}
            {outputLanguages && (
              <div className="bg-black/20 rounded p-2">
                <span className="text-gray-500 block mb-1">Output Languages</span>
                <span className="text-gray-200 font-mono">{toDisplayString(outputLanguages)}</span>
              </div>
            )}

            {/* Formats (for ai-crop) */}
            {formats && (
              <div className="bg-black/20 rounded p-2">
                <span className="text-gray-500 block mb-1">Formats</span>
                <span className="text-gray-200 font-mono">{toDisplayString(formats)}</span>
              </div>
            )}

            {/* Brand Guidelines */}
            {useBrandGuidelines !== undefined && (
              <div className="bg-black/20 rounded p-2">
                <span className="text-gray-500 block mb-1">Brand Guidelines</span>
                <span className={`font-mono ${useBrandGuidelines ? 'text-green-400' : 'text-gray-400'}`}>
                  {useBrandGuidelines ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            )}

            {/* Extending Configs (prefer JSON version) */}
            {extendingConfigs && (
              <div className="bg-black/20 rounded p-2 col-span-2 md:col-span-3">
                <span className="text-gray-500 block mb-1">Extending Configs</span>
                <pre className="text-gray-200 font-mono text-[10px] whitespace-pre-wrap overflow-x-auto max-h-32 overflow-y-auto">
                  {typeof extendingConfigs === 'string' ? extendingConfigs : JSON.stringify(extendingConfigs, null, 2)}
                </pre>
              </div>
            )}

            {/* Other meta fields */}
            {metaEntries.map(([key, value]) => (
              <div key={key} className="bg-black/20 rounded p-2">
                <span className="text-gray-500 block mb-1">{key.replace(/_/g, ' ')}</span>
                <span className="text-gray-200 font-mono text-[10px] truncate block">
                  {toDisplayString(value)}
                </span>
              </div>
            ))}
          </div>

          {/* Prompt Section */}
          {hasPrompt && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs text-gray-400 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Original Prompt
                </label>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddPromptOverride();
                  }}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  + Add Replacement
                </button>
              </div>
              <div className="text-xs text-gray-300 bg-black/30 rounded p-2 font-mono whitespace-pre-wrap">
                {prompt}
              </div>

              {/* Prompt Overrides */}
              {promptOverrides.length > 0 && (
                <div className="space-y-2 mt-2">
                  <label className="text-xs text-yellow-400">Prompt Replacements:</label>
                  {promptOverrides.map((override, overrideIndex) => (
                    <div
                      key={overrideIndex}
                      className="grid grid-cols-[1fr,auto,1fr,auto] gap-2 items-center"
                    >
                      <input
                        type="text"
                        value={override.find}
                        onChange={(e) => onUpdatePromptOverride(overrideIndex, { find: e.target.value })}
                        placeholder="Find text..."
                        className="bg-gray-700 border border-gray-600 text-gray-200 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-purple-500 focus:outline-none font-mono"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="text-gray-500 text-xs">&rarr;</span>
                      <select
                        value={override.replace}
                        onChange={(e) => onUpdatePromptOverride(overrideIndex, { replace: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-gray-700 border border-gray-600 text-gray-200 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-purple-500 focus:outline-none"
                      >
                        <option value="">Select column...</option>
                        {selectedColumns.map(col => (
                          <option key={col} value={`{${col}}`}>{`{${col}}`}</option>
                        ))}
                      </select>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemovePromptOverride(overrideIndex);
                        }}
                        className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Additional Pictures Section - Only show if step has additional pictures */}
          {hasAdditionalPictures && (
            <div className="space-y-2">
              <label className="text-xs text-orange-400 flex items-center gap-1">
                <Image className="w-3 h-3" /> Reference Images (override with BQ columns)
              </label>

              {additionalPictures.map((pic: any, picIndex: number) => {
                // Find the override directly from the passed array
                const override = findOverrideForPicture(picIndex);
                const selectedColumn = override?.column_name || '';

                return (
                  <div
                    key={`${pic.id}-${picIndex}`}
                    className="bg-black/20 rounded p-2 flex items-center gap-3"
                  >
                    <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-300 rounded flex-shrink-0">
                      {pic.category}
                    </span>
                    <span className="text-[10px] text-gray-500 font-mono truncate flex-1">
                      {pic.id}
                    </span>
                    <select
                      value={selectedColumn}
                      onChange={(e) => {
                        e.stopPropagation();
                        const newValue = e.target.value;
                        onUpdateAdditionalPictureOverride(picIndex, {
                          original_id: pic.id,
                          original_category: `step_${stepIndex}_pic_${picIndex}`,
                          source: newValue ? 'column' : 'fixed',
                          column_name: newValue || undefined
                        });
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="bg-gray-700 border border-gray-600 text-gray-200 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-orange-500 focus:outline-none min-w-[150px]"
                    >
                      <option value="">Keep original</option>
                      {selectedColumns.map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}

          {/* No customizable content message */}
          {!hasCustomizableContent && (
            <p className="text-xs text-gray-500 italic">
              This step has no customizable prompt or reference images.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default StepEditor;
